import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Aplicatiile de business - `v4/api/src/Controller/BusinessAppsController.php`.
 *
 * Calea NU poarta `/api/v2` (il pune `apiGet`/`apiPost`):
 *
 *   GET  /business-apps                          lista firmei curente
 *   GET  /business-apps/{id}                     fisa
 *   GET  /business-apps/people/{companyId}       oamenii clientului (proprietar)
 *   POST /business-apps/people/{companyId}       "+ Add a person": contact nou al clientului
 *   POST /business-apps/save                     adauga / modifica
 *   POST /business-apps/{id}/remove              retrage
 *   POST /business-apps/{id}/activate            "Confirm & activate" (pending_review -> active)
 *   POST /business-apps/{id}/reviewed            "Mark reviewed" (termenul urmator, +12 luni)
 *   GET  /business-apps/{id}/candidates?q=       aparate si CI-uri de adaugat
 *   POST /business-apps/{id}/components          adauga o componenta
 *   POST /business-apps/components/{cid}/remove  scoate o componenta
 *   POST /business-apps/{id}/support             suportul + contactele
 *   GET  /business-apps/{id}/copy-sources        de unde se poate copia suportul
 *   POST /business-apps/{id}/copy-support        copiaza de la `from_id`
 *   GET  /business-apps/{id}/support-suggestions precompletarea suportului (doar propuneri)
 *   GET  /business-apps/device/{deviceId}        "Part of" pe fisa aparatului
 *   GET  /easeedesk/tickets/{id}/business-apps   aplicatiile tichetului + emailul pregatit
 *   POST /easeedesk/tickets/{id}/business-apps/escalate
 */

export type Choice = { value: number; label: string }

/** 'down' | 'alarm' | 'degraded' | 'ok' | 'empty' */
export type AppState = 'down' | 'alarm' | 'degraded' | 'ok' | 'empty'

/** Ciclul de viata ITIL (`v4/api/src/BusinessApps/Completeness.php`). */
export type Lifecycle = 'pending_review' | 'active' | 'retired'

/** Un rand din checklist; `section` = unde se completeaza pe fisa. */
export type ChecklistItem = {
  key: string
  label: string
  done: boolean
  section: 'components' | 'details' | 'support' | 'completeness' | 'runbook'
  /** Recomandat (procedura de urgenta): nu intra in `missing`, nu blocheaza activarea. */
  recommended?: boolean
}
export type MissingItem = { key: string; label: string; section: ChecklistItem['section'] }

/** Starea si checklistul, calculate pe server. Lipsesc numai de la un server mai vechi. */
export type Completeness = {
  lifecycle?: Lifecycle
  lifecycleLabel?: string
  origin?: 'manual' | 'discovery'
  reviewedAt?: string | null
  reviewDueAt?: string | null
  reviewDue?: boolean
  checklist?: ChecklistItem[]
  missing?: MissingItem[]
  missingCount?: number
  needsAttention?: boolean
}

export type BusinessAppSummary = Completeness & {
  id: number
  name: string
  vendor: string
  version: string
  criticality: number
  criticalityLabel: string
  companyId: number
  companyName: string
  supportVendor: string
  componentCount: number
  contactCount: number
  state: AppState
  stateLabel: string
  openTickets: number
  openAlarms: number
}

export type BusinessAppsPage = {
  ready: boolean
  message?: string
  apps: BusinessAppSummary[]
  /** Firma aleasa sus - singura ale carei aplicatii se arata, si cu "All companies". */
  companies: { id: number; name: string }[]
  companyId: number
  /** Nicio firma aleasa (sau una la care omul n-are acces): lista e goala, se cere alegerea. */
  needsCompany?: boolean
  canEdit: boolean
  canDelete: boolean
  criticality: Choice[]
  roles: Choice[]
}

export function businessAppsQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'list'],
    queryFn: () => apiGet<BusinessAppsPage>('/business-apps'),
  })
}

export type BusinessAppComponent = {
  id: number
  deviceId: number | null
  ciId: number | null
  name: string
  className: string
  role: number
  roleLabel: string
  note: string
  /** Are muchie `depends_on` in CMDB. */
  linked: boolean
  /** it_ci.health: 1 operational, 2 degraded, 3 down; null fara CI. */
  health: number | null
  healthLabel: string
}

export type SupportContact = {
  id: number
  name: string
  role: string
  phone: string
  email: string
  /** 0 = nu e pe drumul de escaladare; 1, 2, 3... */
  level: number
  levelNote: string
}

export type SupportBlock = {
  vendor: string
  portalUrl: string
  contractNo: string
  /** Zile ISO, 1 = luni .. 7 = duminica. */
  days: number[]
  from: string
  to: string
  timezone: string
  responseHours: number | null
  caseNeeds: string
  scheduleLabel: string
}

export type ImpactTicket = {
  ticketId: number
  subject: string
  alarm: boolean
  created: string | null
  component: string
}

export type BusinessAppDetail = {
  app: BusinessAppSummary & {
    description: string
    notes: string
    ownerContactId: number | null
    ownerName: string
  }
  components: BusinessAppComponent[]
  contacts: SupportContact[]
  support: SupportBlock
  impact: ImpactTicket[]
  cmdb: { ciId: number | null; linked: boolean; propagates: boolean }
  people: CustomerPerson[]
  canEdit: boolean
  canDelete: boolean
  criticality: Choice[]
  roles: Choice[]
}

export function businessAppQuery(id: number) {
  return queryOptions({
    queryKey: ['business-apps', 'detail', id],
    queryFn: () => apiGet<BusinessAppDetail>(`/business-apps/${id}`),
  })
}

/**
 * Un om al clientului, pentru proprietarul aplicatiei. `role` (functia) si
 * `email` deosebesc doi oameni cu acelasi nume; `isContact` = persoana de
 * contact a clientului (vine prima in lista).
 */
export type CustomerPerson = { id: number; name: string; role?: string; email?: string; isContact?: boolean }

export function businessAppPeopleQuery(companyId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'people', companyId],
    queryFn: () => apiGet<{ people: CustomerPerson[] }>(`/business-apps/people/${companyId}`),
    enabled: companyId > 0,
  })
}

/** Persoana scrisa pe loc la "+ Add a person". */
export type NewPerson = { name: string; role: string; email: string; phone: string }

/** O scrie ca persoana de contact a clientului; `id` merge apoi ca `owner_contact_id`. */
export function addBusinessAppPerson(companyId: number, person: NewPerson) {
  return apiPost<Written & { person?: CustomerPerson }>(`/business-apps/people/${companyId}`, person)
}

export type Written = { status: 'OK' | 'ERROR'; message: string; id?: number; linked?: boolean; error?: string }

export function saveBusinessApp(fields: Record<string, PostValue>) {
  return apiPost<Written>('/business-apps/save', fields)
}

export function removeBusinessApp(id: number) {
  return apiPost<Written>(`/business-apps/${id}/remove`, {})
}

export function activateBusinessApp(id: number) {
  return apiPost<Written>(`/business-apps/${id}/activate`, {})
}

export function markBusinessAppReviewed(id: number) {
  return apiPost<Written>(`/business-apps/${id}/reviewed`, {})
}

export type Candidate = { kind: 'device' | 'ci'; id: number; name: string; detail: string }

export function businessAppCandidatesQuery(id: number, search: string) {
  const query = search === '' ? '' : `?q=${encodeURIComponent(search)}`
  return queryOptions({
    queryKey: ['business-apps', 'candidates', id, search],
    queryFn: () => apiGet<{ hits: Candidate[] }>(`/business-apps/${id}/candidates${query}`),
  })
}

export function addBusinessAppComponent(id: number, fields: { kind: 'device' | 'ci'; ref_id: number; role: number; note: string }) {
  return apiPost<Written>(`/business-apps/${id}/components`, fields)
}

export function removeBusinessAppComponent(componentId: number) {
  return apiPost<Written>(`/business-apps/components/${componentId}/remove`, {})
}

export type SupportFields = {
  support_vendor: string
  support_portal_url: string
  support_contract_no: string
  support_days: number[]
  support_from: string
  support_to: string
  support_tz: string
  support_response_hours: string
  support_case_needs: string
  contacts: { name: string; role: string; phone: string; email: string; level: number; levelNote: string }[]
}

export function saveBusinessAppSupport(id: number, fields: SupportFields) {
  return apiPost<Written>(`/business-apps/${id}/support`, fields as unknown as Record<string, PostValue>)
}

export type CopySource = { id: number; name: string; supportVendor: string; companyName: string; contactCount: number }

export function businessAppCopySourcesQuery(id: number) {
  return queryOptions({
    queryKey: ['business-apps', 'copy-sources', id],
    queryFn: () => apiGet<{ sources: CopySource[] }>(`/business-apps/${id}/copy-sources`),
  })
}

export function copyBusinessAppSupport(id: number, fromId: number) {
  return apiPost<Written>(`/business-apps/${id}/copy-support`, { from_id: fromId })
}

/** De unde vine o propunere: `label` se arata langa camp ("from Opera PMS"). */
export type SuggestionSource = { kind: 'app' | 'peers' | 'detected' | 'catalog' | 'easeedesk'; label: string }

export type Suggested<T> = { value: T; source: SuggestionSource }

/**
 * Precompletarea suportului - `SupportSuggestions` in API. Numai ce lipseste
 * din aplicatie; null = nimic de propus. Nu se scrie nimic pana la Save.
 */
export type SupportSuggestions = {
  ready: boolean
  vendor: Suggested<string> | null
  portalUrl: Suggested<string> | null
  schedule: { days: number[]; from: string; to: string; timezone: string; source: SuggestionSource } | null
  responseHours: Suggested<number> | null
  caseNeeds: Suggested<string> | null
  contacts: { rows: { name: string; role: string; phone: string; email: string; level: number; levelNote: string }[]; source: SuggestionSource } | null
}

export function businessAppSupportSuggestionsQuery(id: number) {
  return queryOptions({
    queryKey: ['business-apps', 'support-suggestions', id],
    queryFn: () => apiGet<SupportSuggestions>(`/business-apps/${id}/support-suggestions`),
    /* O propunere lipsa nu opreste fereastra: o eroare = fara propuneri, imediat. */
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/* -------------------------------------------------------------------------
 * fisa aparatului
 * ---------------------------------------------------------------------- */

export type DevicePartOf = {
  apps: { id: number; name: string; role: number; roleLabel: string; criticality: number; criticalityLabel: string }[]
}

export function deviceBusinessAppsQuery(deviceId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'device', deviceId],
    queryFn: () => apiGet<DevicePartOf>(`/business-apps/device/${deviceId}`),
    enabled: deviceId > 0,
  })
}

/* -------------------------------------------------------------------------
 * escaladarea din tichet
 * ---------------------------------------------------------------------- */

export type TicketEscalationApp = {
  id: number
  name: string
  vendor: string
  version: string
  criticalityLabel: string
  support: SupportBlock
  /** Numele de afisat al furnizorului de suport (support vendor, altfel producatorul). */
  supportName: string
  components: { name: string; roleLabel: string; note: string }[]
  contacts: SupportContact[]
  draft: { subject: string; body: string }
  /** Termenul daca s-ar trimite acum, `Y-m-d H:i`; null fara timp de raspuns. */
  dueIfNow: string | null
  dueBasis: 'support_hours' | 'calendar' | 'none'
}

export type TicketEscalationRecord = {
  id: number
  appId: number
  appName: string
  supportName: string
  level: number
  contactName: string
  to: string
  subject: string
  dueAt: string | null
  dueBasis: string
  overdue: boolean
  createdAt: string
  sentByEmail: boolean
}

export type TicketBusinessApps = {
  ready: boolean
  ticketId?: number
  canEscalate: boolean
  apps: TicketEscalationApp[]
  escalations: TicketEscalationRecord[]
}

export function ticketBusinessAppsQuery(ticketId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'ticket', ticketId],
    queryFn: () => apiGet<TicketBusinessApps>(`/easeedesk/tickets/${ticketId}/business-apps`),
    enabled: ticketId > 0,
  })
}

export type EscalationWritten = Written & { mode?: 'send' | 'record'; dueAt?: string | null; dueBasis?: string; caseId?: number }

export function escalateToVendor(
  ticketId: number,
  fields: {
    app_id: number
    contact_id: number
    to: string
    cc: string
    subject: string
    body: string
    mode: 'send' | 'record'
    /** Nivelul urmator al unui caz deschis (pasul 2). */
    case_id?: number
    wait_third_party?: string
    external_provider?: number
    escalation_reason?: number
  },
) {
  return apiPost<EscalationWritten>(`/easeedesk/tickets/${ticketId}/business-apps/escalate`, fields)
}
